/**
 * Security Controls Processor
 * Handles ACL and role configuration for Now Assist Skills
 *
 * Extracts configuration from UserAccessConfig and creates sys_security_acl records
 * Supports both role names (e.g., "admin") and sys_ids for flexibility
 * ACL name follows pattern: {sanitized_skill_name}.{$id}
 */

import { isGUID, type ObjectShape, type Record, type Shape } from '@servicenow/sdk-build-core'
import type { Context } from '@servicenow/sdk-build-core'
import { NowIdShape } from '@servicenow/sdk-build-plugins'
import {
    AGENT_ACCESS_ROLE_CONFIG_DEFAULTS,
    SECURITY_TABLE_NAMES,
    ACL_DEFAULTS,
    ACL_CONSTANTS,
    DEFAULT_ADMIN_ROLE_SYS_ID,
    RESTRICTED_ROLE_NAMES,
    RESTRICTED_ROLE_SYS_IDS,
} from './defaults'
import { sanitizeSkillName } from './skill-plugin-helpers'

const { SYS_AGENT_ACCESS_ROLE_CONFIGURATION, SYS_AGENT_ACCESS_ROLE_MAPPING, SYS_USER_ROLE } = SECURITY_TABLE_NAMES

// Use string literals for standard ServiceNow ACL tables (handled by acl-plugin.ts)
const SYS_SECURITY_ACL = 'sys_security_acl'
const SYS_SECURITY_ACL_ROLE = 'sys_security_acl_role'

/**
 * Process security controls for a skill
 * Creates role configuration records
 *
 * @param securityControls - Security controls configuration from the API
 * @param skillName - Name of the skill
 * @param skillRecordId - Generated skill record sys_id (used for ACL name to match internal_name pattern and agent reference)
 * @param arg - Argument shape for transform
 * @param factory - Factory for creating records
 * @returns Array of created security records
 */
export async function processSecurityControls(
    securityControls: ObjectShape,
    skillName: string,
    skillRecordId: string,
    arg: ObjectShape,
    factory: Context['factory'],
    diagnostics: Context['diagnostics']
): Promise<Record[]> {
    const records: Record[] = []

    // Security controls are mandatory - validate presence
    if (!securityControls || !securityControls.isObject()) {
        diagnostics.error(arg, 'securityControls is mandatory and must be provided')
    }

    // Extract User Access configuration - simple object with type field
    const userAccess = securityControls.get('userAccess')
    if (!userAccess || !userAccess.isObject()) {
        diagnostics.error(securityControls, 'securityControls.userAccess is required and must be an object')
    }

    const userAccessObj = userAccess.asObject()
    const accessType = userAccessObj.get('type').asString().getValue()

    // $id is mandatory in type definitions - TypeScript enforces this at compile time
    const aclExplicitId = userAccessObj.get('$id') as NowIdShape | undefined

    // Validate access type
    if (accessType !== 'authenticated' && accessType !== 'roles') {
        diagnostics.error(
            userAccessObj,
            `securityControls.userAccess.type must be 'authenticated' or 'roles'. Got: ${accessType}`
        )
    }

    // Extract roles — mandatory for 'roles' type, optional for 'authenticated' type
    let aclRoles: Shape[] = []
    let securityAttribute = ''

    if (accessType === 'authenticated') {
        securityAttribute = ACL_CONSTANTS.USER_IS_AUTHENTICATED
    }

    // Extract roles for both access types
    // For 'roles': mandatory (must have at least one)
    // For 'authenticated': optional (can have zero or more)
    const rolesField = userAccessObj.get('roles')
    if (accessType === 'roles') {
        if (!rolesField.isArray()) {
            diagnostics.error(userAccessObj, 'securityControls.userAccess.roles must be an array when type is "roles"')
        }
        aclRoles = rolesField.asArray().getElements()
        if (aclRoles.length === 0) {
            diagnostics.error(rolesField, 'securityControls.userAccess.roles must contain at least one role')
        }
    } else if (accessType === 'authenticated' && rolesField && rolesField.isArray()) {
        aclRoles = rolesField.asArray().getElements()
    }

    // Validate that restricted roles (e.g., 'maint') are not used
    if (aclRoles.length > 0) {
        for (const role of aclRoles) {
            if (role.isString()) {
                const roleValue = role.asString().getValue()
                // Check against restricted role names
                if (RESTRICTED_ROLE_NAMES.includes(roleValue as (typeof RESTRICTED_ROLE_NAMES)[number])) {
                    diagnostics.error(
                        rolesField,
                        `The '${roleValue}' role cannot be used for ACL roles. ` +
                            `This role has special system-level privileges and is restricted.`
                    )
                }
                // Check against restricted role sys_ids
                if (RESTRICTED_ROLE_SYS_IDS.includes(roleValue as (typeof RESTRICTED_ROLE_SYS_IDS)[number])) {
                    diagnostics.error(
                        rolesField,
                        `The 'maint' role (sys_id: ${roleValue}) cannot be used for ACL roles. ` +
                            `This role has special system-level privileges and is restricted.`
                    )
                }
            }
        }
    }

    // Create sys_security_acl record for gen_ai_skill
    // Format: {sanitized_skill_name}.{sys_id} (matches skill's internal_name pattern)
    const sanitizedSkillName = sanitizeSkillName(skillName)
    const aclName = `${sanitizedSkillName}.${skillRecordId}`

    const aclRecord = await factory.createRecord({
        source: securityControls,
        table: SYS_SECURITY_ACL,
        ...(aclExplicitId instanceof NowIdShape ? { explicitId: aclExplicitId } : {}),
        properties: arg.transform(({ $ }) => ({
            name: $.val(aclName),
            type: $.val(ACL_CONSTANTS.GEN_AI_SKILL_TYPE),
            operation: $.val(ACL_CONSTANTS.OPERATION_EXECUTE),
            active: $.val(ACL_DEFAULTS.active),
            admin_overrides: $.val(ACL_DEFAULTS.admin_overrides),
            advanced: $.val(ACL_DEFAULTS.advanced),
            decision_type: $.val(ACL_DEFAULTS.decision_type),
            local_or_existing: $.val(ACL_DEFAULTS.local_or_existing),
            security_attribute: $.val(securityAttribute),
        })),
    })
    records.push(aclRecord)

    // Create sys_security_acl_role records for each role
    // Supports both role names (e.g., "admin") and sys_ids
    if (aclRoles.length > 0) {
        for (const role of aclRoles) {
            // Create role reference - supports role name or sys_id
            const roleReference = role.isString()
                ? await factory.createReference({
                      source: role,
                      table: SYS_USER_ROLE,
                      keys: isGUID(role.asString().getValue()) ? undefined : { name: role.asString().getValue() },
                      guid: isGUID(role.asString().getValue()) ? role.asString().getValue() : undefined,
                  })
                : role

            const aclRoleRecord = await factory.createRecord({
                source: role,
                table: SYS_SECURITY_ACL_ROLE,
                properties: {
                    sys_security_acl: aclRecord,
                    sys_user_role: roleReference,
                },
            })
            records.push(aclRoleRecord)
        }
    }

    // Role configuration uses two independent fields with strict per-field semantics:
    //   - roleRestrictions → role_list (legacy glide_list, sys_ids only)
    //   - roleMap          → sys_agent_access_role_mapping (M2M reference, names only)
    // No cross-field mixing or auto-conversion. At least one must be non-empty.

    // ---- roleRestrictions (sys_ids → role_list) ----
    const roleRestrictionsField = securityControls.get('roleRestrictions')
    const roleRestrictionsArray = roleRestrictionsField?.ifArray()?.getElements() ?? []
    const roleListSysIds: string[] = []
    for (const role of roleRestrictionsArray) {
        if (role.isString()) {
            const roleValue = role.asString().getValue()
            if (isGUID(roleValue)) {
                roleListSysIds.push(roleValue)
            } else {
                // roleRestrictions takes sys_ids only — names are not resolvable in the
                // glide_list column type that backs role_list.
                diagnostics.error(
                    roleRestrictionsField,
                    `roleRestrictions must contain only role sys_ids (GUIDs), not role names. ` +
                        `Got: '${roleValue}'. Use the role sys_id when using roleRestrictions ` +
                        `(e.g., '${DEFAULT_ADMIN_ROLE_SYS_ID}' for admin).`
                )
            }
        } else {
            // Handle ExplicitKey / Now.ID references — extract the sys_id value.
            const roleId = role.isRecordId?.() ? role.getValue() : role.toString()
            if (roleId && typeof roleId === 'string') {
                roleListSysIds.push(roleId)
            }
        }
    }

    // ---- roleMap (names or sys_user_role refs → sys_agent_access_role_mapping) ----
    // Accepts string names (user-authored) or Shape RecordIds (from transform). Mirrors the
    // userAccess.roles pattern so Shape identity is preserved through the round-trip and the
    // SDK's keys-registry can auto-track the underlying sys_user_role references.
    const roleMapField = securityControls.get('roleMap')
    const roleMapArray = roleMapField?.ifArray()?.getElements() ?? []
    // Items to emit: each is either a validated string name or the original Shape.
    const validatedRoleMapItems: Shape[] = []
    for (const role of roleMapArray) {
        if (role.isString()) {
            const roleValue = role.asString().getValue()
            if (isGUID(roleValue)) {
                // roleMap targets the M2M reference field; sys_ids belong in roleRestrictions.
                diagnostics.error(
                    roleMapField,
                    `roleMap must contain role names, not sys_ids. ` +
                        `Got: '${roleValue}'. Use the role name (e.g., 'admin'), ` +
                        `or move the sys_id to securityControls.roleRestrictions.`
                )
            } else if (RESTRICTED_ROLE_NAMES.includes(roleValue as (typeof RESTRICTED_ROLE_NAMES)[number])) {
                // Restricted-role name check (e.g., 'maint').
                diagnostics.error(
                    roleMapField,
                    `The '${roleValue}' role cannot be used in roleMap. ` +
                        `This role has special system-level privileges and is restricted.`
                )
            } else {
                validatedRoleMapItems.push(role)
            }
        } else {
            // Shape (RecordId/Role/DbRecord) from a regenerated transform — pass through.
            // The Shape carries its coalesce key (role name) so build's createReference flow
            // resolves it correctly via the registry without losing identity.
            validatedRoleMapItems.push(role)
        }
    }

    // ---- At-least-one validation ----
    if (roleListSysIds.length === 0 && validatedRoleMapItems.length === 0) {
        diagnostics.error(
            securityControls,
            'securityControls must have at least one of roleRestrictions or roleMap defined.'
        )
    }

    // ---- Emit parent sys_agent_access_role_configuration record ----
    const roleListValue = roleListSysIds.join(',')
    const roleConfigRecord = await factory.createRecord({
        source: securityControls,
        table: SYS_AGENT_ACCESS_ROLE_CONFIGURATION,
        properties: arg.transform(({ $ }) => ({
            name: $.val(skillName),
            agent: $.val(skillRecordId),
            agent_table: $.val(AGENT_ACCESS_ROLE_CONFIG_DEFAULTS.agent_table),
            action: $.val(AGENT_ACCESS_ROLE_CONFIG_DEFAULTS.action),
            // roleRestrictions → role_list. Empty string when only roleMap is used.
            role_list: $.val(roleListValue),
        })),
    })
    records.push(roleConfigRecord)

    // ---- Emit one sys_agent_access_role_mapping row per roleMap entry ----
    // Mirrors the ACL sys_security_acl_role pattern: when the input is a string, create a
    // fresh reference with the name as the coalesce key; when it's already a Shape (RecordId
    // from a transform round-trip), pass it through unchanged so its identity is preserved
    // in the keys registry.
    for (const role of validatedRoleMapItems) {
        const roleReference = role.isString()
            ? await factory.createReference({
                  source: role,
                  table: SYS_USER_ROLE,
                  keys: { name: role.asString().getValue() },
              })
            : role
        const mappingRecord = await factory.createRecord({
            source: role,
            table: SYS_AGENT_ACCESS_ROLE_MAPPING,
            properties: {
                agent_access_config: roleConfigRecord,
                role: roleReference,
            },
        })
        records.push(mappingRecord)
    }

    return records
}
